import { getIconComponent } from "@storytell/bsky-icons";
import { dispatchGlobalEvent } from "@storytell/events";
import type { Asset, AssetId, Concept, ConceptId, Label, LabelId, View, ViewId } from "@storytell/types";
import { StTooltip, StTooltipTrigger } from "@storytell/ui/tooltip";
import { TbAlertTriangle, TbApps, TbFileText, TbLoader2, TbStack, TbX } from "solid-icons/tb";
import { type Component, For, Match, Show, Switch } from "solid-js";
import { Dynamic } from "solid-js/web";
import { twMerge } from "tailwind-merge";
import { LabelTooltip } from "~/components/labels/LabelTooltip";
import { isLightColor } from "~/components/layouts/minimal/isLightColor";
import { lifecycleStateGrouped } from "~/components/utils/lifecycleStateMappers";
import { truncateMiddle } from "~/utils/truncateMiddle";
import { useWire } from "~/wire";

type UploadingAssetLike = {
  id: string;
  status: string;
  filename: string;
};

interface ScopedItemsListProps {
  views: () => View[];
  labels: () => Label[];
  concepts: () => Concept[];
  assets: () => Asset[];
  uploadingAssets?: () => UploadingAssetLike[];

  allowRemove?: boolean;
  onRemoveView?: (id: ViewId) => void;
  onRemoveLabel?: (id: LabelId) => void;
  onRemoveConcept?: (id: ConceptId) => void;
  onRemoveAsset?: (id: AssetId) => void;
}

const truncateFilename = (filename: string, maxLength = 25) => {
  if (filename.length <= maxLength) return filename;
  const extension = filename.split(".").pop();
  const nameWithoutExt = filename.substring(0, filename.lastIndexOf("."));
  const truncatedName = nameWithoutExt.substring(0, maxLength - (extension ? extension.length : 0) - 4);
  return `${truncatedName}...${extension ?? ""}`;
};

const truncateConceptName = (name: string, maxLength = 25) => {
  if (name.length <= maxLength) return name;
  return `${name.substring(0, maxLength)}...`;
};

export const ScopedItemsList: Component<ScopedItemsListProps> = (props) => {
  const wire = useWire();
  const allowRemove = () => props.allowRemove !== false;

  const totalItems = () =>
    (props.assets()?.length ?? 0) +
    (props.concepts()?.length ?? 0) +
    (props.labels()?.length ?? 0) +
    (props.views()?.length ?? 0);

  const handleViewClick = (view: View, e: MouseEvent) => {
    // Don't navigate if clicking the remove button
    if ((e.target as HTMLElement).closest("button")) return;
    e.preventDefault();
    // Clear any selected concept first to ensure we show the assets table
    dispatchGlobalEvent("clear-selected-concept", undefined);
    // Set the tab first, then the view to ensure correct order
    // Both methods navigate, which opens the pane if it's not open
    wire.knowledgeUi.setActiveTab("assets");
    // Use a small delay to ensure setActiveTab completes before setActiveView
    setTimeout(() => {
      wire.knowledgeUi.setActiveView(view.id);
    }, 0);
  };

  const handleLabelClick = (label: Label, e: MouseEvent) => {
    // Don't navigate if clicking the remove button
    if ((e.target as HTMLElement).closest("button")) return;
    e.preventDefault();
    // Clear any selected concept first to ensure we show the assets table
    dispatchGlobalEvent("clear-selected-concept", undefined);
    // Dispatch event - the handler in KnowledgePane will open the pane and apply the filter
    dispatchGlobalEvent("open-label-in-assets-pane", { labelId: label.id });
  };

  const handleConceptClick = (concept: Concept, e: MouseEvent) => {
    // Don't navigate if clicking the remove button
    if ((e.target as HTMLElement).closest("button")) return;
    e.preventDefault();
    wire.knowledgeUi.open();
    wire.knowledgeUi.setActiveTab("concepts");
    dispatchGlobalEvent("open-concept-in-pane", { conceptId: concept.id });
  };

  const handleAssetClick = (asset: Asset, e: MouseEvent) => {
    // Don't navigate if clicking the remove button
    if ((e.target as HTMLElement).closest("button")) return;
    e.preventDefault();
    // Clear any selected concept first to ensure we show the assets table
    dispatchGlobalEvent("clear-selected-concept", undefined);
    wire.knowledgeUi.open();
    wire.knowledgeUi.setActiveTab("assets");
    dispatchGlobalEvent("open-asset-in-knowledge-pane", { assetId: asset.id });
  };

  const handleUploadingAssetClick = (asset: UploadingAssetLike, e: MouseEvent) => {
    // Don't navigate if clicking the remove button
    if ((e.target as HTMLElement).closest("button")) return;
    e.preventDefault();
    // For uploading assets, we can't navigate to them yet, but we can open the assets pane
    wire.knowledgeUi.open();
    wire.knowledgeUi.setActiveTab("assets");
  };

  return (
    <>
      <div class="flex items-center gap-2 flex-auto max-w-[calc(100%_-_16rem)] overflow-x-auto scrollbar-hide">
        <For each={props.views()}>
          {(view) => (
            <span
              onClick={(e) => handleViewClick(view, e)}
              class="group/btn shrink-0 flex bg-canvas hover:bg-canvas-hover border border-primary-border rounded-full items-center gap-1 py-1 px-2 select-none cursor-pointer"
            >
              <TbStack class="size-3 group-hover/btn:hidden group-active/btn:scale-active transition-transform text-secondary-text" />
              <Show when={allowRemove()}>
                <StTooltip
                  placement="top"
                  contentProps={{
                    class: "z-50",
                  }}
                  content={<p>Remove</p>}
                >
                  <StTooltipTrigger
                    as="button"
                    type="button"
                    aria-label={`Remove ${view.name} from chat`}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (props.onRemoveView) props.onRemoveView(view.id);
                    }}
                    class="hidden group-hover/btn:flex items-center gap-1"
                  >
                    <TbX class="size-3 cursor-pointer hover:scale-hover active:scale-active transition-transform text-secondary-text hover:[stroke-width:3] hover:drop-shadow-sm" />
                    <span class="text-secondary-text pointer-events-none cursor-default"> | </span>
                  </StTooltipTrigger>
                </StTooltip>
              </Show>
              <span class="hover:font-bold">{truncateConceptName(view.name)}</span>
            </span>
          )}
        </For>

        <For each={props.labels()}>
          {(label) => (
            <span
              onClick={(e) => handleLabelClick(label, e)}
              class="group/btn shrink-0 flex bg-canvas hover:bg-canvas-hover border border-primary-border rounded-full items-center gap-1 py-1 px-2 select-none cursor-pointer"
            >
              <span
                class="rounded-full size-4 grid place-content-center bg-[var(--label-color)] group-hover/btn:hidden"
                style={{
                  "--label-color": label.color,
                  "--label-icon": isLightColor(label.color || "") ? "black" : "white",
                }}
              >
                <Dynamic
                  component={getIconComponent(label.icon)}
                  class="text-[var(--label-icon)] transition-transform"
                />
              </span>
              <Show when={allowRemove()}>
                <StTooltip
                  placement="top"
                  contentProps={{
                    class: "z-50",
                  }}
                  content={<p>Remove</p>}
                >
                  <StTooltipTrigger
                    as="button"
                    type="button"
                    aria-label={`Remove ${label.name} from chat`}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (props.onRemoveLabel) props.onRemoveLabel(label.id);
                    }}
                    class="hidden group-hover/btn:flex items-center gap-1"
                  >
                    <TbX class="size-3 cursor-pointer hover:scale-hover active:scale-active transition-transform text-secondary-text hover:[stroke-width:3] hover:drop-shadow-sm" />
                    <span class="text-secondary-text pointer-events-none cursor-default"> | </span>
                  </StTooltipTrigger>
                </StTooltip>
              </Show>
              <span class="hover:font-bold">{truncateMiddle(label.name)}</span>
            </span>
          )}
        </For>

        <For each={props.concepts()}>
          {(concept) => (
            <span
              onClick={(e) => handleConceptClick(concept, e)}
              class="group/btn shrink-0 flex bg-concept-bg text-concept-text hover:bg-concept-bg-hover border border-concept-border rounded-full items-center gap-1 py-1 px-2 select-none cursor-pointer"
            >
              <TbApps class="size-3 group-hover/btn:hidden group-active/btn:scale-active transition-transform text-concept-icon" />
              <Show when={allowRemove()}>
                <StTooltip
                  placement="top"
                  contentProps={{
                    class: "z-50",
                  }}
                  content={<p>Remove</p>}
                >
                  <StTooltipTrigger
                    as="button"
                    type="button"
                    aria-label={`Remove ${concept.name} from chat`}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (props.onRemoveConcept) props.onRemoveConcept(concept.id);
                    }}
                    class="hidden group-hover/btn:flex items-center gap-1"
                  >
                    <TbX class="size-3 cursor-pointer hover:scale-hover active:scale-active transition-transform text-secondary-text hover:[stroke-width:3] hover:drop-shadow-sm" />
                    <span class="text-secondary-text pointer-events-none cursor-default"> | </span>
                  </StTooltipTrigger>
                </StTooltip>
              </Show>
              <span class="hover:font-bold">{truncateConceptName(concept.name)}</span>
            </span>
          )}
        </For>

        <For each={props.uploadingAssets ? props.uploadingAssets() : []}>
          {(asset) => (
            <span
              onClick={(e) => handleUploadingAssetClick(asset, e)}
              class="group/btn shrink-0 flex bg-asset-bg hover:bg-asset-bg-hover text-asset-text border border-asset-border rounded-full items-center gap-1 py-1 px-2 select-none cursor-pointer"
            >
              <Switch>
                <Match when={asset.status === "uploading"}>
                  <TbLoader2 class="w-3 h-3 animate-spin group-hover/btn:hidden group-active/btn:scale-active transition-transform text-asset-icon" />
                </Match>
                <Match when={asset.status === "error"}>
                  <TbAlertTriangle class="w-3 h-3 text-red-700 dark:text-red-400 group-hover/btn:hidden group-active/btn:scale-active transition-transform" />
                </Match>
              </Switch>
              <Show when={allowRemove()}>
                <StTooltip
                  placement="top"
                  contentProps={{
                    class: "z-50",
                  }}
                  content={<p>Remove</p>}
                >
                  <StTooltipTrigger
                    as="button"
                    type="button"
                    aria-label={`Remove ${asset.filename} from chat`}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (props.onRemoveAsset) props.onRemoveAsset(asset.id as AssetId);
                    }}
                    class="hidden group-hover/btn:flex items-center gap-1"
                  >
                    <TbX class="size-3 cursor-pointer hover:scale-hover active:scale-active transition-transform text-secondary-text hover:[stroke-width:3] hover:drop-shadow-sm" />
                    <span class="text-secondary-text pointer-events-none cursor-default"> | </span>
                  </StTooltipTrigger>
                </StTooltip>
              </Show>
              <span class="text-xs hover:font-bold">{truncateFilename(asset.filename)}</span>
            </span>
          )}
        </For>

        <For each={props.assets()}>
          {(asset) => {
            const assetName = (
              (asset as unknown as { fileName?: string; filename?: string }).fileName ||
              (asset as unknown as { filename?: string }).filename ||
              "asset"
            ).toString();
            return (
              <span
                onClick={(e) => handleAssetClick(asset, e)}
                class="group/btn shrink-0 flex bg-asset-bg hover:bg-asset-bg-hover text-asset-text border border-asset-border rounded-full items-center gap-1 py-1 px-2 select-none cursor-pointer"
              >
                <Switch>
                  <Match when={["uploading", "processing"].includes(lifecycleStateGrouped(asset.status.state))}>
                    <TbLoader2 class="w-3 h-3 animate-spin group-hover/btn:hidden group-active/btn:scale-active transition-transform text-asset-icon" />
                  </Match>
                  <Match when={lifecycleStateGrouped(asset.status.state) === "error"}>
                    <TbAlertTriangle class="w-3 h-3 text-red-700 dark:text-red-400 group-hover/btn:hidden group-active/btn:scale-active transition-transform" />
                  </Match>
                  <Match when={lifecycleStateGrouped(asset.status.state) === "ready"}>
                    <TbFileText class="w-3 h-3 group-hover/btn:hidden group-active/btn:scale-active transition-transform text-asset-icon" />
                  </Match>
                </Switch>
                <Show when={allowRemove()}>
                  <StTooltip
                    placement="top"
                    contentProps={{
                      class: "z-50",
                    }}
                    content={<p>Remove</p>}
                  >
                    <StTooltipTrigger
                      as="button"
                      type="button"
                      aria-label={`Remove ${assetName} from chat`}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        if (props.onRemoveAsset) props.onRemoveAsset(asset.id as AssetId);
                      }}
                      class="hidden group-hover/btn:flex items-center gap-1"
                    >
                      <TbX class="size-3 cursor-pointer hover:scale-hover active:scale-active transition-transform text-secondary-text hover:[stroke-width:3] hover:drop-shadow-sm" />
                      <span class="text-secondary-text pointer-events-none cursor-default"> | </span>
                    </StTooltipTrigger>
                  </StTooltip>
                </Show>
                <span class="hover:font-bold">
                  {truncateFilename(
                    (asset as unknown as { fileName?: string; filename?: string }).fileName ||
                      (asset as unknown as { filename?: string }).filename ||
                      "",
                  )}
                </span>
              </span>
            );
          }}
        </For>
      </div>
    </>
  );
};
